import axios from 'axios';
import { QueryRequest, QueryResponse, DocumentStats, SyncStatus } from '../types';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 120000, // 2 minutes timeout for API calls
});

// Add request interceptor for logging
api.interceptors.request.use((config) => {
  console.log(`API Request: ${config.method?.toUpperCase()} ${config.url}`);
  // If sending FormData, let the browser set the correct multipart boundary
  if (config.data instanceof FormData) {
    if (config.headers) {
      delete (config.headers as any)['Content-Type'];
    }
  }
  return config;
});

// Add response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('API Error:', error.response?.data || error.message);
    return Promise.reject(error);
  }
);

export const apiService = {
  // Query endpoints
  async askQuestion(request: QueryRequest): Promise<QueryResponse> {
    const response = await api.post('/api/v1/query/ask', request);
    return response.data;
  },

  // Streaming query endpoint
  async askQuestionStream(
    request: QueryRequest,
    onMetadata: (data: { sources: any[]; intent_analysis: any; session_id: string }) => void,
    onContent: (text: string) => void,
    onDone: (response_time_ms: number) => void,
    onError: (error: string) => void
  ): Promise<void> {
    const url = `${API_BASE_URL}/api/v1/query/ask/stream`;
    
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('No response body');
      }

      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              
              if (data.type === 'metadata') {
                onMetadata({
                  sources: data.sources,
                  intent_analysis: data.intent_analysis,
                  session_id: data.session_id
                });
              } else if (data.type === 'content') {
                onContent(data.text);
              } else if (data.type === 'done') {
                onDone(data.response_time_ms);
              } else if (data.type === 'error') {
                onError(data.message);
              }
            } catch (e) {
              console.error('Error parsing SSE data:', e);
            }
          }
        }
      }
    } catch (error) {
      console.error('Streaming error:', error);
      onError(error instanceof Error ? error.message : 'Unknown error');
    }
  },

  async getSuggestions(): Promise<string[]> {
    const response = await api.get('/api/v1/query/suggestions');
    return response.data.suggestions;
  },

  async getQueryHistory(sessionId: string): Promise<any[]> {
    const response = await api.get(`/api/v1/query/history/${sessionId}`);
    return response.data.queries;
  },

  async deleteChatHistory(sessionId: string): Promise<any> {
    const response = await api.delete(`/api/v1/query/history/${sessionId}`);
    return response.data;
  },

  // Document endpoints
  async getDocumentStats(): Promise<DocumentStats> {
    const response = await api.get('/api/v1/documents/stats');
    return response.data;
  },

  async getDocuments(params?: {
    source_type?: string;
    status?: string;
    search?: string;
    page?: number;
    per_page?: number;
  }): Promise<any> {
    const response = await api.get('/api/v1/documents/', { params });
    return response.data;
  },

  // Source endpoints
  async getSyncStatus(): Promise<SyncStatus[]> {
    const response = await api.get('/api/v1/sources/sync-status');
    return response.data.sources;
  },

  async triggerSync(sourceType: string): Promise<void> {
    await api.post(`/api/v1/sources/sync`, { source_type: sourceType });
  },

  // Health check
  async healthCheck(): Promise<any> {
    const response = await api.get('/health');
    return response.data;
  },

  // Generic HTTP methods for flexibility
  async get(url: string, params?: any): Promise<any> {
    const response = await api.get(url, { params });
    return response;
  },

  async post(url: string, data?: any): Promise<any> {
    const response = await api.post(url, data);
    return response;
  },

  // Explicit helper for multipart form posts
  async postForm(url: string, formData: FormData): Promise<any> {
    const response = await api.post(url, formData, {
      headers: { /* let Axios set Content-Type with boundary automatically */ },
    });
    return response;
  },

  async put(url: string, data?: any): Promise<any> {
    const response = await api.put(url, data);
    return response;
  },

  async delete(url: string): Promise<any> {
    const response = await api.delete(url);
    return response;
  },
};

export default apiService; 